import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import type {
  AnalyticsAggregateRequest,
  AnalyticsAggregateRow,
  Coverage,
  SegmentDefinition,
} from "@adlywise/shared";
import { requestAggregate } from "@/features/analytics/aggregateApi";
import { REQUEST_CANCEL_VISIBILITY_DELAY_MS } from "@/features/analytics/constants";
import {
  effectiveSortBy,
  type AnalyticsConfig,
} from "@/features/analytics/components/ConfigurePanel";

type PickerRange = { from: Date; to: Date };

type UseAnalyticsAggregateParams = {
  config: AnalyticsConfig;
  range: PickerRange;
  filter: SegmentDefinition;
};

type UseAnalyticsAggregateResult = {
  rows: AnalyticsAggregateRow[];
  truncated: boolean;
  // "analytics copy" while the money columns are the ClickHouse copy of the
  // money board (labels.money from the API); null when no money was asked.
  moneyLabel: string | null;
  // What the report did not cover (null before a run or on an error).
  coverage: Coverage | null;
  hasRun: boolean;
  // The config the shown rows were produced by. The table renders from THIS,
  // never from the live config: after a source / bucket / grouping change the
  // retained rows carry the old run's columns, and reading them through the new
  // config's headings would show them as somebody else's numbers (a missing
  // count renders as 0). null before the first successful run.
  resultConfig: AnalyticsConfig | null;
  // The request body of that same run (its range, filter, ...): what the run
  // summary and the export describe. null before the first finished run.
  resultRequest: AnalyticsAggregateRequest | null;
  // True when the panel's current settings would send a different request than
  // the one behind the shown rows ("settings changed — run to update").
  isStale: boolean;
  isLoading: boolean;
  isRequestCancelable: boolean;
  isRunReportButtonDisabled: boolean;
  runReportButtonLabel: string;
  errorMessage: string;
  handleRunReportButtonClick: () => void;
};

// The range is a pair of INCLUSIVE calendar days: To = 2026-10-26 includes all
// of the 26th. The backend (range.ts) reads the Kyiv calendar day of each
// instant and turns it into [Kyiv midnight of From, Kyiv midnight of the day
// after To), so all it needs is an instant that lands on the picked day in
// Kyiv. Noon UTC of the day the user picked does, from any browser timezone
// (Kyiv is UTC+2/+3, so noon UTC is 14:00/15:00 there). The local-midnight
// instant this used to send only did that at or west of Kyiv: from Tokyo,
// local midnight of the 26th is 15:00 UTC on the 25th — the 25th in Kyiv —
// which dropped the last picked day and added one before the first.
export const toRangeInstants = (
  range: PickerRange,
): { from: string; to: string } => ({
  from: `${format(range.from, "yyyy-MM-dd")}T12:00:00.000Z`,
  to: `${format(range.to, "yyyy-MM-dd")}T12:00:00.000Z`,
});

// The conditions that filter anything: not disabled, and carrying at least one
// value. A condition card the user opened but has not filled in stays in the
// panel's state and is never sent — the backend skips it as a predicate, but it
// still counts toward which tables it reads (an empty isPaid condition would
// turn on the request_raw join for a responses report). 0, false and "" are
// values; only null/undefined and an empty list are "no value".
const hasValue = (value: unknown): boolean =>
  Array.isArray(value)
    ? value.length > 0
    : value !== null && value !== undefined;

const activeConditions = (
  conditions: NonNullable<SegmentDefinition["all"]>,
): NonNullable<SegmentDefinition["all"]> =>
  conditions.filter((c) => c.disabled !== true && hasValue(c.value));

const orderByFor = (
  config: AnalyticsConfig,
): Pick<AnalyticsAggregateRequest, "orderBy"> => {
  const defaults = ["bucket", ...config.groupBy] as const;
  const listed = effectiveSortBy(config);
  const full = [...listed, ...defaults.filter((k) => !listed.includes(k))];
  return full.every((k, i) => k === defaults[i]) ? {} : { orderBy: full };
};

// The request body a run sends for these settings. Pure, so the run and the
// staleness check build it the same way, and so it can be tested.
export const buildAggregateRequest = ({
  config,
  range,
  filter,
}: UseAnalyticsAggregateParams): AnalyticsAggregateRequest => ({
  source: config.source,
  groupBy: config.groupBy,
  // Only when it differs from the default (time, then groupBy), completed the
  // way the backend completes it, so an order moved back to the default — or
  // any two edits that sort the same — make the same request.
  ...orderByFor(config),
  bucket: config.bucket,
  range: toRangeInstants(range),
  // Include-only / AND-locked: the Filters section only writes the "all"
  // group. Send just that so `any`/`none` never leak into the request, and
  // only its active conditions (see activeConditions).
  filter: { all: activeConditions(filter.all ?? []) },
  // statuses are responses-only (each selected status → one metric
  // group). Omit entirely on requests (fixed `requests` metric group),
  // and omit when a counter metric is selected: the counters are a
  // different grain and the backend refuses the mix. Sending them anyway
  // would make every counter report a guaranteed 400, because
  // `config.statuses` is never empty.
  ...(config.source === "responses" && config.counters.length === 0
    ? { statuses: config.statuses }
    : {}),
  // Money is responses-only, and mixes with counters no better.
  ...(config.source === "responses" &&
  config.counters.length === 0 &&
  config.money.length > 0
    ? { money: config.money }
    : {}),
  // Counters come from the per-minute aggregate and are answered without
  // touching a row table, so they are independent of `source`. The panel
  // has already restricted groupBy to what the counters can break down
  // by, and the backend rejects the combination otherwise.
  ...(config.counters.length > 0 ? { counters: config.counters } : {}),
});

// A request body reduced to what changes the answer, as a string: condition
// ids dropped, its AND-ed conditions and their `in` values sorted (order means
// nothing there), and object keys sorted. groupBy, statuses, money and counters
// keep their order: it is the column order.
const canonical = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonical);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [
          key,
          canonical((value as Record<string, unknown>)[key]),
        ]),
    );
  }
  return value;
};

const sortedStrings = (values: unknown[]): string[] =>
  values.map((v) => JSON.stringify(v)).sort();

export const requestSignature = (
  request: AnalyticsAggregateRequest,
): string => {
  // The body is already reduced to its active conditions (buildAggregateRequest),
  // so this only removes what is order- or UI-only: ids and condition order.
  const conditions = (request.filter.all ?? [])
    .map((c) => {
      const values = Array.isArray(c.value) ? c.value : [c.value];
      return { field: c.field, op: c.op, value: sortedStrings(values) };
    })
    .map((c) => JSON.stringify(canonical(c)))
    .sort();
  return JSON.stringify(canonical({ ...request, filter: { all: conditions } }));
};

// Whether the shown rows answer the panel's current settings. Compared on the
// request signature rather than the config, so a change that cannot alter the
// answer (a status chip hidden behind a counter, an empty condition, a
// reordered filter) does not flag the results.
export const isResultStale = (
  submitted: AnalyticsAggregateRequest | null,
  live: AnalyticsAggregateRequest,
): boolean =>
  submitted !== null && requestSignature(submitted) !== requestSignature(live);

export const useAnalyticsAggregate = ({
  config,
  range,
  filter,
}: UseAnalyticsAggregateParams): UseAnalyticsAggregateResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestCancelable, setIsRequestCancelable] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [rows, setRows] = useState<AnalyticsAggregateRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [moneyLabel, setMoneyLabel] = useState<string | null>(null);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [hasRun, setHasRun] = useState(false);
  // What the shown rows answer: the config and request body of the run that
  // produced them, set in the same update as the rows.
  const [submitted, setSubmitted] = useState<{
    config: AnalyticsConfig;
    request: AnalyticsAggregateRequest;
  } | null>(null);
  const requestAbortControllerRef = useRef<AbortController | null>(null);
  const cancelVisibilityTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (cancelVisibilityTimeoutRef.current !== null) {
        window.clearTimeout(cancelVisibilityTimeoutRef.current);
      }
      requestAbortControllerRef.current?.abort();
    };
  }, []);

  const clearCancelVisibilityTimeout = (): void => {
    if (cancelVisibilityTimeoutRef.current !== null) {
      window.clearTimeout(cancelVisibilityTimeoutRef.current);
      cancelVisibilityTimeoutRef.current = null;
    }
  };

  const handleRunReport = async (): Promise<void> => {
    const abortController = new AbortController();
    // Snapshot the settings now: the panel stays editable while the request is
    // in flight, and its answer belongs to these settings, not to whatever the
    // panel shows when it lands.
    const snapshot = {
      config,
      request: buildAggregateRequest({ config, range, filter }),
    };

    requestAbortControllerRef.current = abortController;
    setIsLoading(true);
    setIsRequestCancelable(false);
    setErrorMessage("");

    cancelVisibilityTimeoutRef.current = window.setTimeout(() => {
      if (requestAbortControllerRef.current === abortController) {
        setIsRequestCancelable(true);
      }
    }, REQUEST_CANCEL_VISIBILITY_DELAY_MS);

    try {
      const response = await requestAggregate(snapshot.request, {
        signal: abortController.signal,
      });
      // A superseded request (cancelled, or replaced by a later run) never
      // renders, even if its response beat the abort.
      if (requestAbortControllerRef.current !== abortController) return;

      setSubmitted(snapshot);
      setRows(response.rows);
      setTruncated(response.truncated);
      setMoneyLabel(response.labels?.money ?? null);
      setCoverage(response.coverage ?? null);
      setHasRun(true);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      if (requestAbortControllerRef.current !== abortController) return;

      setSubmitted(snapshot);
      setRows([]);
      setTruncated(false);
      setMoneyLabel(null);
      setCoverage(null);
      setHasRun(true);
      setErrorMessage("Request failed. Check browser console and network tab.");
      console.error("analytics/aggregate request failed", error);
    } finally {
      // Only the request that still owns the shared refs tears them down: a
      // cancelled request settling late must not clear the timer (or the
      // controller) of the run that replaced it, or that run never becomes
      // cancelable. A cancelled request's timer was cleared by the cancel.
      if (requestAbortControllerRef.current === abortController) {
        clearCancelVisibilityTimeout();
        requestAbortControllerRef.current = null;
        setIsLoading(false);
        setIsRequestCancelable(false);
      }
    }
  };

  const handleCancelRequest = (): void => {
    clearCancelVisibilityTimeout();
    requestAbortControllerRef.current?.abort();
    requestAbortControllerRef.current = null;
    setIsLoading(false);
    setIsRequestCancelable(false);
  };

  const handleRunReportButtonClick = (): void => {
    if (isLoading && isRequestCancelable) {
      handleCancelRequest();
      return;
    }

    if (isLoading) {
      return;
    }

    void handleRunReport();
  };

  return {
    rows,
    truncated,
    moneyLabel,
    coverage,
    hasRun,
    resultConfig: submitted?.config ?? null,
    resultRequest: submitted?.request ?? null,
    isStale: isResultStale(
      submitted?.request ?? null,
      buildAggregateRequest({ config, range, filter }),
    ),
    isLoading,
    isRequestCancelable,
    isRunReportButtonDisabled: isLoading && !isRequestCancelable,
    runReportButtonLabel:
      isLoading && isRequestCancelable ? "Cancel request" : "Run report",
    errorMessage,
    handleRunReportButtonClick,
  };
};
